import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

/** Focusable elements inside the panel, in DOM order, for the focus trap. */
function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null);
}

/**
 * Right-side slide-over panel that renders above the calendar via position:fixed.
 *
 * Accessibility: role="dialog", aria-modal, labelled close button, Esc key, and a
 * focus trap — focus moves into the panel on open, Tab/Shift+Tab cycle within it,
 * and focus is restored to the triggering element on close.
 *
 * Backdrop click also closes the panel (click on the overlay, not the panel itself).
 */
export function SlideOver({
  open,
  title,
  onClose,
  children,
  dismissable = true,
  wide = false,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** When false, a backdrop click or Esc does NOT close the panel — only the ✕
   * button does. Used for the composer so a stray click can't discard a draft. */
  dismissable?: boolean;
  /** A roomier panel that reads as a dedicated page rather than a slim drawer. */
  wide?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  // The element focused before the panel opened, restored when it closes so
  // keyboard users return to where they were.
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Close on Esc while the panel is open (unless the panel is non-dismissable).
  useEffect(() => {
    if (!open || !dismissable) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose, dismissable]);

  // Focus trap: focus the first control on open, keep Tab within the panel, and
  // restore focus to the trigger on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel) {
      const first = focusablesIn(panel)[0];
      // Fall back to the panel itself so focus is never left outside the dialog.
      (first ?? panel).focus();
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !panel) return;
      const items = focusablesIn(panel);
      if (items.length === 0) return;
      const firstEl = items[0]!;
      const lastEl = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === firstEl || active === panel)) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    panel?.addEventListener("keydown", onKeyDown);
    return () => {
      panel?.removeEventListener("keydown", onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Prevent body scroll while the panel is open — avoids jarring shifts when
  // long pages scroll underneath the overlay.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    // The overlay is the backdrop. Clicking it (not the panel) closes.
    <div
      className="so-overlay"
      role="presentation"
      onClick={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`so-panel${wide ? " so-panel--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="so-head">
          <span className="so-title">{title}</span>
          <button
            type="button"
            className="so-close ghost small"
            onClick={onClose}
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>
        <div className="so-body">{children}</div>
      </div>
    </div>
  );
}
